import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PurchasesNav from '../components/PurchasesNav';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { purchaseOrderService } from '../services/api';

function notifyApprovalsChanged() {
  window.dispatchEvent(new CustomEvent('purchase-approvals-updated'));
}

function materialLabel(p) {
  // Багатопозиційні закупівлі: матеріали в позиціях (поля шапки порожні)
  const items = Array.isArray(p.items) ? p.items : [];
  if (items.length > 0) {
    const names = items.map((it) => (
      it.raw_material_name || it.dye_name || it.packaging_material_name ||
      it.consumable_material_name || it.press_form_identifier ||
      (it.equipment_name || '').trim() || '?'
    ));
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
  }
  const eq = (p.equipment_name || '').trim();
  if (eq) return eq;
  if (p.raw_material_name) return p.raw_material_name;
  if (p.dye_name) return p.dye_name;
  if (p.packaging_material_name) return p.packaging_material_name;
  if (p.consumable_material_name) return p.consumable_material_name;
  if (p.press_form_identifier) return p.press_form_identifier;
  return '—';
}

function PurchaseApprovalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightPoId = useMemo(() => {
    const r = searchParams.get('highlight_po');
    if (r != null && /^\d+$/.test(String(r).trim())) return Number(r.trim());
    return null;
  }, [searchParams]);

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const approvalFilter = tab === 0 ? 'pending' : tab === 1 ? 'rejected' : null;
      const params = approvalFilter ? { approval_status: approvalFilter } : {};
      const res = await purchaseOrderService.getAll(params);
      const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setRows(list);
      setError('');
    } catch (e) {
      console.error(e);
      setError(e.response?.status === 403 ? 'Немає доступу до погодження закупівель' : 'Не вдалося завантажити список');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (highlightPoId == null || loading) return;
    if (rows.length === 0) return;

    const row = rows.find((x) => Number(x.id) === highlightPoId);
    if (!row) {
      const next = new URLSearchParams(searchParams);
      next.delete('highlight_po');
      setSearchParams(next, { replace: true });
      return;
    }

    if (row.approval_status === 'pending' && tab !== 0) {
      setTab(0);
      return;
    }
    if (row.approval_status === 'rejected' && tab !== 1) {
      setTab(1);
      return;
    }
    if (row.approval_status === 'approved' && tab !== 2) {
      setTab(2);
      return;
    }

    const t = window.requestAnimationFrame(() => {
      const el = document.getElementById(`approval-po-row-${highlightPoId}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const next = new URLSearchParams(searchParams);
      next.delete('highlight_po');
      setSearchParams(next, { replace: true });
    });
    return () => window.cancelAnimationFrame(t);
  }, [highlightPoId, loading, rows, tab, searchParams, setSearchParams]);

  const openDetail = (p) => {
    setDetail(p);
    setComment((p.approval_comment || '').trim());
  };

  const closeDetail = () => {
    setDetail(null);
    setComment('');
  };

  const submitDecision = async (status) => {
    if (!detail) return;
    try {
      setSaving(true);
      await purchaseOrderService.patch(detail.id, {
        approval_status: status,
        approval_comment: (comment || '').trim(),
      });
      notifyApprovalsChanged();
      closeDetail();
      load();
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data, null, 2));
      alert(msg || e.message || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  if (loading && rows.length === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PurchasesNav />
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <FactCheckIcon color="primary" />
        <Typography variant="h4">Погодження закупівель</Typography>
      </Box>
      <Typography color="text.secondary" paragraph sx={{ maxWidth: 720 }}>
        Нові закупівлі з&apos;являються тут до статусу «Погоджено». Поки закупівля очікує погодження, її не можна
        змінювати в розділі «Закупівлі» та не можна приймати на склад пов&apos;язані надходження.
      </Typography>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Очікують" />
        <Tab label="Відхилені" />
        <Tab label="Усі" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Матеріал</TableCell>
              <TableCell>Постачальник</TableCell>
              <TableCell>Кількість</TableCell>
              <TableCell>Статус закупівлі</TableCell>
              <TableCell>Погодження</TableCell>
              <TableCell>Дата закупівлі</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">Немає записів</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((p) => (
                <TableRow
                  key={p.id}
                  id={`approval-po-row-${p.id}`}
                  hover
                  sx={
                    highlightPoId === p.id
                      ? (theme) => ({ bgcolor: theme.palette.action.selected })
                      : undefined
                  }
                >
                  <TableCell>{p.id}</TableCell>
                  <TableCell>{materialLabel(p)}</TableCell>
                  <TableCell>{(p.supplier_name || '').trim() || '—'}</TableCell>
                  <TableCell>{p.quantity}</TableCell>
                  <TableCell>{p.status_display || p.status}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.approval_status_display || p.approval_status}
                      color={
                        p.approval_status === 'approved'
                          ? 'success'
                          : p.approval_status === 'rejected'
                          ? 'error'
                          : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {p.purchase_date ? new Date(p.purchase_date).toLocaleDateString('uk-UA') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => openDetail(p)}>
                      Рішення
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(detail)} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle>
          Погодження закупівлі #{detail?.id}
          {detail ? (
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {materialLabel(detail)} · {detail.quantity} од.
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Коментар (для відхилення бажано пояснити причину)"
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {detail?.approval_status === 'pending' ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Після «Погодити» закупівлю можна редагувати, змінювати статус та приймати на склад.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} disabled={saving}>
            Закрити
          </Button>
          {detail?.approval_status === 'pending' ? (
            <>
              <Button color="error" disabled={saving} onClick={() => submitDecision('rejected')}>
                Відхилити
              </Button>
              <Button variant="contained" color="success" disabled={saving} onClick={() => submitDecision('approved')}>
                Погодити
              </Button>
            </>
          ) : null}
          {detail?.approval_status === 'rejected' ? (
            <Button variant="contained" disabled={saving} onClick={() => submitDecision('approved')}>
              Погодити після виправлень
            </Button>
          ) : null}
          {detail?.approval_status === 'approved' ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              Закупівля вже погоджена.
            </Typography>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseApprovalsPage;
